import moment from 'moment';
import 'moment/locale/uk';

export default {
  methods: {
    /**
     * Returns a Moment.js object for a given date, or the current date if no date is provided.
     * Sets the locale to 'uk'.
     * @param {string | Date | object} [date] - The date to create a Moment.js object from.
     * @param format
     * @returns {object} A Moment.js object.
     */
    getMoment(date, format) {
      const m = format ? moment(date, format) : moment(date);
      m.locale('uk');
      return m;
    },

    /**
     * Converts a Moment.js object to a native JavaScript Date object.
     * This method is still useful if you need to pass a native Date object
     * to other libraries or APIs that don't accept Moment objects.
     * @param {object} momentObject - The Moment.js object to convert.
     * @returns {Date} The native JavaScript Date object.
     */
    toDateMoment(momentObject) {
      return momentObject.toDate();
    },

    /**
     * Checks if two Moment.js objects or dates are the same on a given unit.
     * @param {object | string | Date} date1 - The first date.
     * @param {object | string | Date} date2 - The second date.
     * @param {string} unit - The unit to compare (e.g., 'day', 'month', 'year', 'week').
     * @returns {boolean} True if the dates are the same on the given unit, false otherwise.
     */
    isSameMoment(date1, date2, unit) {
      const m1 = this.getMoment(date1);
      const m2 = this.getMoment(date2);
      return m1.isSame(m2, unit);
    },

    /**
     * Checks if the first Moment.js object or date is same or before the second one on a given unit.
     * @param {object | string | Date} date1 - The first date.
     * @param {object | string | Date} date2 - The second date.
     * @param {string} unit - The unit to compare (e.g., 'day', 'month', 'year', 'week').
     * @returns {boolean} True if the first date is same or before, false otherwise.
     */
    isSameOrBeforeMoment(date1, date2, unit) {
      const m1 = this.getMoment(date1);
      const m2 = this.getMoment(date2);
      return m1.isSameOrBefore(m2, unit);
    },

    /**
     * Checks if the first Moment.js object or date is same or after the second one on a given unit.
     * @param {object | string | Date} date1 - The first date.
     * @param {object | string | Date} date2 - The second date.
     * @param {string} unit - The unit to compare (e.g., 'day', 'month', 'year', 'week').
     * @returns {boolean} True if the first date is same or after, false otherwise.
     */
    isSameOrAfterMoment(date1, date2, unit) {
      const m1 = this.getMoment(date1);
      const m2 = this.getMoment(date2);
      return m1.isSameOrAfter(m2, unit);
    },

    /**
     * Checks if the first Moment.js object or date is after the second one on a given unit.
     * @param {object | string | Date} date1 - The first date.
     * @param {object | string | Date} date2 - The second date.
     * @param {string} unit - The unit to compare (e.g., 'day', 'month', 'year', 'week').
     * @returns {boolean} True if the first date is after, false otherwise.
     */
    isAfterMoment(date1, date2, unit) {
      const m1 = this.getMoment(date1);
      const m2 = this.getMoment(date2);
      return m1.isAfter(m2, unit);
    },

    /**
     * Checks if the first Moment.js object or date is before the second one on a given unit.
     * @param {object | string | Date} date1 - The first date.
     * @param {object | string | Date} date2 - The second date.
     * @param {string} unit - The unit to compare (e.g., 'day', 'month', 'year', 'week').
     * @returns {boolean} True if the first date is before, false otherwise.
     */
    isBeforeMoment(date1, date2, unit) {
      const m1 = this.getMoment(date1);
      const m2 = this.getMoment(date2);
      return m1.isBefore(m2, unit);
    },

    /**
     * Clones a Moment.js object or creates a Moment.js object from a date and then clones it.
     * This is useful to ensure you're working with a new instance that won't mutate the original.
     * @param {object | string | Date} date - The date to clone.
     * @returns {object} A cloned Moment.js object.
     */
    cloneMoment(date) {
      const m = this.getMoment(date);
      return m.clone();
    },

    /**
     * Adds a specified amount of time to a Moment.js object.
     * Returns a new Moment.js object with the added time (does not mutate the original).
     * @param {object | string | Date} date - The date to add to.
     * @param {number} amount - The amount to add.
     * @param {string} unit - The unit of time to add (e.g., 'days', 'weeks', 'months', 'years').
     * @returns {object} A new Moment.js object with the added time.
     */
    addMoment(date, amount, unit) {
      const m = this.getMoment(date);
      return m.clone().add(amount, unit);
    },

    /**
     * Subtracts a specified amount of time from a Moment.js object.
     * Returns a new Moment.js object with the subtracted time (does not mutate the original).
     * @param {object | string | Date} date - The date to subtract from.
     * @param {number} amount - The amount to subtract.
     * @param {string} unit - The unit of time to subtract (e.g., 'days', 'weeks', 'months', 'years').
     * @returns {object} A new Moment.js object with the subtracted time.
     */
    subtractMoment(date, amount, unit) {
      const m = this.getMoment(date);
      return m.clone().subtract(amount, unit);
    },

    /**
     * Sets the unit of a Moment.js object to the start of that unit.
     * Returns a new Moment.js object set to the start of the unit (does not mutate the original).
     * @param {object | string | Date} date - The date to set to the start of the unit.
     * @param {string} unit - The unit to set to the start of (e.g., 'month', 'week', 'day').
     * @returns {object} A new Moment.js object set to the start of the unit.
     */
    startOfMoment(date, unit) {
      const m = this.getMoment(date);
      return m.startOf(unit);
    },

    /**
     * Sets the unit of a Moment.js object to the end of that unit.
     * Returns a new Moment.js object set to the end of the unit (does not mutate the original).
     * @param {object | string | Date} date - The date to set to the end of the unit.
     * @param {string} unit - The unit to set to the end of (e.g., 'month', 'week', 'day').
     * @returns {object} A new Moment.js object set to the end of the unit.
     */
    endOfMoment(date, unit) {
      const m = this.getMoment(date);
      return m.endOf(unit);
    },

    /**
     * Formats a Moment.js object into a string.
     * @param {object | string | Date} date - The date to format.
     * @param {string} formatString - The format string (e.g., 'MMMM', 'YYYY-MM-DD', 'gggg-WW' for ISO week).
     * @returns {string} The formatted date string.
     */
    formatMoment(date, formatString) {
      const m = this.getMoment(date);
      return m.format(formatString);
    },

    /**
     * Gets the year of a Moment.js object.
     * @param {object | string | Date} date - The date to get the year from.
     * @returns {number} The year.
     */
    getYearMoment(date) {
      const m = this.getMoment(date);
      return m.year();
    },

    /**
     * Sets the year of a Moment.js object.
     * Returns a new Moment.js object with the year set (does not mutate the original).
     * @param {object | string | Date} date - The date to set the year for.
     * @param {number} year - The year to set.
     * @returns {object} A new Moment.js object with the year set.
     */
    setYearMoment(date, year) {
      const m = this.getMoment(date);
      return m.clone().year(year);
    },

    /**
     * Gets the month index (0-11) of a Moment.js object.
     * @param {object | string | Date} date - The date to get the month from.
     * @returns {number} The month index (0-11).
     */
    getMonthMoment(date) {
      const m = this.getMoment(date);
      return m.month();
    },

    /**
     * Sets the month of a Moment.js object.
     * Returns a new Moment.js object with the month set (does not mutate the original).
     * @param {object | string | Date} date - The date to set the month for.
     * @param {number} monthIndex - The month index (0-11) to set.
     * @returns {object} A new Moment.js object with the month set.
     */
    setMonthMoment(date, monthIndex) {
      const m = this.getMoment(date);
      return m.clone().month(monthIndex);
    },

    /**
     * Gets the day of the month of a Moment.js object.
     * @param {object | string | Date} date - The date to get the day from.
     * @returns {number} The day of the month (1-31).
     */
    getDateMoment(date) {
      const m = this.getMoment(date);
      return m.date();
    },

    /**
     * Gets the ISO week number of a Moment.js object.
     * @param {object | string | Date} date - The date to get the week number from.
     * @returns {number} The ISO week number (1-53).
     */
    getWeekMoment(date) {
      const m = this.getMoment(date);
      return m.isoWeek();
    },

    /**
     * Gets the ISO week-numbering year of a Moment.js object.
     * The ISO week-numbering year is the year to which the week belongs,
     * based on the ISO 8601 standard (week starts on Monday, first week contains Jan 4th).
     * @param {object | string | Date} date - The date to get the ISO week year from.
     * @returns {number} The ISO week-numbering year.
     */
    getISOWeekYearMoment(date) {
      const m = this.getMoment(date);
      return m.isoWeekYear();
    },

    /**
     * Checks if a specific date is explicitly disabled by the 'disabledDates' prop.
     * This method does NOT consider range selection or maxRangeDays.
     * @param {object} date - The date to check.
     * @returns {boolean} True if the date is explicitly disabled, false otherwise.
     */
    isDateExplicitlyDisabled(date) {
      return this.disabledDates.some((disabledDateItem) => {
        const comparableDate = disabledDateItem.date || disabledDateItem;
        return this.isSameMoment(date, comparableDate, 'day');
      });
    },

    /**
     * Checks if a date should be disabled, considering explicit disabled dates and maxRangeDays limit.
     * This is used for individual day selection/highlighting.
     * @param {object} date - The date to check.
     * @returns {boolean} True if the date is disabled, false otherwise.
     */
    isDisabledDate(date) {
      if (this.isDateExplicitlyDisabled(date)) {
        return true;
      }

      if (this.range && this.selectingRange && this.maxRangeDays !== null && this.startDate) {
        if (this.isBeforeMoment(date, this.startDate, 'day')) {
          return true;
        }

        let tempStartDate = this.getMoment(this.startDate);
        let tempEndDate = this.getMoment(date);

        if (tempStartDate.isAfter(tempEndDate, 'day')) {
          [tempStartDate, tempEndDate] = [tempEndDate, tempStartDate];
        }

        const daysInPotentialRange = this.getDaysDifference(tempStartDate, tempEndDate);
        return daysInPotentialRange > this.maxRangeDays;
      }

      return false;
    },

    /**
     * Checks if an entire week should be disabled, considering explicit disabled dates,
     * range selection, and maxRangeDays limit. Used for week view.
     * @param {object} weekStartDate - The Moment.js object representing the start of the week.
     * @returns {boolean} True if the week is disabled, false otherwise.
     */
    isDisabledWeek(weekStartDate) {
      for (let i = 0; i < 7; i++) {
        const dayInWeek = this.addMoment(weekStartDate, i, 'days');
        if (this.isDateExplicitlyDisabled(dayInWeek)) {
          return true;
        }
      }

      if (this.range && this.selectingRange && this.startDate) {
        if (this.isBeforeMoment(weekStartDate, this.startDate, 'week')) {
          return true;
        }

        if (this.maxRangeDays !== null) {
          let testStart = this.getMoment(this.startDate);
          let testEnd = this.endOfMoment(weekStartDate, 'week');

          if (testStart.isAfter(testEnd, 'day')) {
            testStart = this.getMoment(weekStartDate);
            testEnd = this.getMoment(this.startDate);
          }

          const daysInPotentialRange = this.getDaysDifference(testStart, testEnd);
          if (daysInPotentialRange > this.maxRangeDays) {
            return true;
          }
        }
      }
      return false;
    },

    /**
     * Gets the number of ISO weeks in a given year.
     * @param {number} year - The year to check.
     * @returns {number} The number of ISO weeks (52 or 53).
     */
    getISOWeeksInYear(year) {
      const dec31 = moment(`${year}-12-31`, 'YYYY-MM-DD');
      return dec31.isoWeek() === 1 ? 52 : dec31.isoWeek();
    },

    /**
     * Calculates the difference in days between two Moment.js objects.
     * @param {object} moment1 - The first Moment.js object.
     * @param {object} moment2 - The second Moment.js object.
     * @returns {number} The difference in days.
     */
    getDaysDifference(moment1, moment2) {
      const m1 = this.getMoment(moment1);
      const m2 = this.getMoment(moment2);
      return Math.abs(m1.diff(m2, 'days'));
    },
  },
};
